<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <!-- HEADER -->
      <div class="modal-header">
        <h3>🗑 Sản phẩm đã xoá</h3>
        <button class="btn-close" @click="$emit('close')">×</button>
      </div>

      <!-- BODY -->
      <div class="modal-body">
        <div v-if="loading" class="loading-overlay">
          <!-- <div class="spinner"></div>
          <p>Đang tải dữ liệu...</p> -->
          <div class="spinner"></div>
          <div class="skeleton-card" v-for="i in 3" :key="i"></div>
        </div>

        <div v-if="!loading && products.length === 0" class="empty-state">
          <p>Không có sản phẩm nào đã xoá.</p>
        </div>
        <!-- PRODUCT INFO -->
        <div v-for="item in products" :key="item.id" class="product-card">
          <div class="product-header">
            <h5>{{ item.ten_hang }}</h5>
            <span class="badge deleted">Đã xoá</span>
          </div>

          <div class="product-info">
            <div><strong>Mã hàng:</strong> {{ item.ma_hang }}</div>
            <div><strong>Loại hàng:</strong> {{ item.loai_hang }}</div>
            <div>
              <strong>Giá bán:</strong> {{ formatCurrency(item.gia_ban) }}
            </div>
            <div><strong>Tồn kho:</strong> {{ item.ton_kho }}</div>
          </div>

          <div class="deleted-info">
            <span>🕒 Đã xoá lúc: {{ formatDate(item.deleted_at) }}</span>
          </div>
          <div class="footer">
            <button
              class="btn-restore"
              :disabled="loading"
              @click="restoreProduct(item.id)"
            >
              ♻ Khôi phục
            </button>

            <button class="btn-delete" @click="forceDelete(item.id)">
              ❌ Xoá vĩnh viễn
            </button>
          </div>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="modal-footer">
        <button class="btn-cancel" @click="$emit('close')">Đóng</button>
      </div>
    </div>
    <ConfirmDialog
      v-if="showConfirm"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :type="confirmDialog.type"
      @confirm="confirmDialog.onConfirm"
      @cancel="showConfirm = false"
    />
  </div>
</template>

<script setup>
import { useToast } from "@/composables/useToast";
import { productApi } from "@/services/api";
import { formatCurrency, formatDate } from "@/utils/helpers";
import { onMounted, reactive, ref } from "vue";
import ConfirmDialog from "../ConfirmDialog.vue";

const emit = defineEmits(["save", "close"]);
const loading = ref(false);
const toast = useToast();
const showConfirm = ref(false);
const confirmDialog = reactive({
  title: "",
  message: "",
  type: "danger",
  onConfirm: () => {},
});
const products = ref([]);

const fetchTrashProducts = async () => {
  loading.value = true;
  try {
    const res = await productApi.getTrashProduct();
    products.value = res.data.data.data || res.data;
  } catch (error) {
    const errorMsg = error.response?.data?.error || "Có lỗi xảy ra";
    toast.error(errorMsg);
  } finally {
    loading.value = false;
  }
};

const restoreProduct = (id) => {
  confirmDialog.title = "Khôi phục";
  confirmDialog.message = "Bạn có muốn khôi phục sản phẩm này không?";
  confirmDialog.type = "info";
  showConfirm.value = true;
  confirmDialog.onConfirm = async () => {
    loading.value = true;
    try {
      const res = await productApi.restoreProduct(id);
      if (res.data.success) {
        toast.success("Đã khôi phục sản phẩm");
        fetchTrashProducts();
        emit("save");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Có lỗi xảy ra";
      console.log(error);
      toast.error(errorMsg);
    } finally {
      loading.value = false;
      showConfirm.value = false;
    }
  };
};

const forceDelete = (id) => {
  confirmDialog.title = "Xoá vĩnh viễn";
  confirmDialog.message =
    "Bạn có chắc muốn xoá sản phẩm này vĩnh viễn không? Hành động này không thể hoàn tác.";
  confirmDialog.type = "danger";
  showConfirm.value = true;
  confirmDialog.onConfirm = async () => {
    loading.value = true;
    try {
      const res = await productApi.forceDeleteProduct(id);
      if (res.data.success) {
        toast.success("Đã xoá sản phẩm vĩnh viễn");
        fetchTrashProducts();
        emit("save");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Có lỗi xảy ra";
      console.log(error);
      toast.error(errorMsg);
    } finally {
      loading.value = false;
      showConfirm.value = false;
    }
  };
};

onMounted(fetchTrashProducts);
</script>
<style scoped>
.product-card {
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 14px;
  background: #fafafa;
  margin: 10px 0;
  transition: 0.2s;
}
.product-card:hover {
  box-shadow: 0 2px 8px black;
}

.product-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.product-header h4 {
  margin: 0;
}
/* badge */
.badge.deleted {
  background: #ef4444;
  color: #fff;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 12px;
}

/* info */
.product-info div {
  margin-bottom: 4px;
  font-size: 14px;
}

/* deleted info */
.deleted-info {
  margin-top: 10px;
  font-size: 12px;
  color: #6b7280;
}

/* footer */
.footer {
  padding: 12px;
  margin-top: 5px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  border-top: 1px solid #eee;
}

/* buttons */
.btn-cancel {
  background: #e5e7eb;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
}

.btn-restore {
  background: #10b981;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
}

.btn-delete {
  background: #ef4444;
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  z-index: 10;
  border-radius: 10px;
}

/* spinner */
.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #42b983;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 8px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

</style>
